import { api } from '../axios'
import type { ApiResponse, Document } from '@/types'
import type {
  GetDocumentsParams,
  GetDocumentsResponse,
  UploadDocumentData,
  BulkUploadDocumentData,
  UpdateDocumentData,
  DocumentWithUrl,
  DownloadUrlResponse,
} from './document.static'

export const documentService = {
  async getDocuments(params?: GetDocumentsParams): Promise<GetDocumentsResponse> {
    const response = await api.get<ApiResponse<GetDocumentsResponse>>('/documents', { params })
    return response.data.data
  },

  async getDocument(id: string): Promise<DocumentWithUrl> {
    const response = await api.get<ApiResponse<DocumentWithUrl>>(`/documents/${id}`)
    return response.data.data
  },

  async uploadDocument(data: UploadDocumentData): Promise<Document> {
    const formData = new FormData()
    formData.append('file', data.file)
    formData.append('title', data.title)
    formData.append('documentType', data.documentType)
    if (data.patientId) formData.append('patientId', data.patientId)
    if (data.staffId) formData.append('staffId', data.staffId)
    if (data.description) formData.append('description', data.description)
    if (data.documentDate) formData.append('documentDate', data.documentDate)

    const response = await api.post<ApiResponse<Document>>('/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data.data
  },

  async bulkUploadDocuments(data: BulkUploadDocumentData): Promise<Document[]> {
    const formData = new FormData()
    data.files.forEach((file) => formData.append('files', file))
    data.documentTypes.forEach((type) => formData.append('documentTypes', type))
    data.descriptions.forEach((d) => formData.append('descriptions', d))
    data.documentDates.forEach((d) => formData.append('documentDates', d))
    if (data.patientId) formData.append('patientId', data.patientId)
    if (data.staffId) formData.append('staffId', data.staffId)

    const response = await api.post<ApiResponse<Document[]>>('/documents/bulk', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data.data
  },

  async updateDocument(id: string, data: UpdateDocumentData): Promise<Document> {
    const response = await api.patch<ApiResponse<Document>>(`/documents/${id}`, data)
    return response.data.data
  },

  async archiveDocument(id: string): Promise<Document> {
    const response = await api.patch<ApiResponse<Document>>(`/documents/${id}/archive`)
    return response.data.data
  },

  async unarchiveDocument(id: string): Promise<Document> {
    const response = await api.patch<ApiResponse<Document>>(`/documents/${id}/unarchive`)
    return response.data.data
  },

  async deleteDocument(id: string): Promise<void> {
    await api.delete(`/documents/${id}`)
  },

  async getDownloadUrl(id: string): Promise<DownloadUrlResponse> {
    const response = await api.get<ApiResponse<DownloadUrlResponse>>(`/documents/${id}/download`)
    return response.data.data
  },
}
